class ProductMetafieldGallerySequence extends HTMLElement {
  static AUTO_SCROLL_MS = 3000;

  connectedCallback() {
    this.carousel = this.querySelector('scroll-carousel');
    this._timer = null;
    this._jumping = false;
    this.lightbox = null;
    this.lightboxIndex = 0;
    this.activeIndex = 0;

    if (!this.carousel) {
      return;
    }

    this.originalItems = [...this.carousel.querySelectorAll('.product-metafield-gallery__item')];
    this.originalCount = this.originalItems.length;

    if (!this.originalCount) {
      return;
    }

    this.#setupInfiniteLoop();
    this.#bindItemInteractions();

    this._onVisibilityChange = () => this.#syncAutoScroll();
    this._onCarouselChange = (event) => {
      if (this._jumping) {
        return;
      }

      const index = event.detail?.index;

      if (typeof index === 'number') {
        this.activeIndex = index;
      }
    };

    this._onCarouselSettle = () => this.#normalizeLoopPosition();

    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this.carousel.addEventListener('carousel:change', this._onCarouselChange);
    this.carousel.addEventListener('carousel:settle', this._onCarouselSettle);
    this.carousel.addEventListener('pointerdown', () => this.#stopAutoScroll());
    this.carousel.addEventListener('touchstart', () => this.#stopAutoScroll(), { passive: true });
    this.carousel.addEventListener('pointerup', () => this.#scheduleAutoScroll());
    this.carousel.addEventListener('touchend', () => this.#scheduleAutoScroll(), { passive: true });
    this.carousel.addEventListener('pointercancel', () => this.#scheduleAutoScroll());

    this.#syncAutoScroll();
  }

  disconnectedCallback() {
    this.#stopAutoScroll();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this.carousel?.removeEventListener('carousel:change', this._onCarouselChange);
    this.carousel?.removeEventListener('carousel:settle', this._onCarouselSettle);
    this.#destroyLightbox();
  }

  get items() {
    return this.originalItems;
  }

  #setupInfiniteLoop() {
    // Need more items than the largest visible count (6 on desktop) for a seamless loop
    if (this.originalCount <= 6) {
      return;
    }

    // Duplicate the set once so the end can scroll into the start seamlessly
    this.originalItems.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('img[loading="eager"]').forEach((img) => {
        img.setAttribute('loading', 'lazy');
      });
      this.carousel.appendChild(clone);
    });
  }

  #bindItemInteractions() {
    this.carousel.querySelectorAll('.product-metafield-gallery__item').forEach((item) => {
      let pointerStartX = 0;
      let pointerStartY = 0;
      let didDrag = false;

      item.addEventListener('pointerdown', (event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        didDrag = false;
      });

      item.addEventListener('pointermove', (event) => {
        if (
          Math.abs(event.clientX - pointerStartX) > 6 ||
          Math.abs(event.clientY - pointerStartY) > 6
        ) {
          didDrag = true;
        }
      });

      item.addEventListener('click', (event) => {
        if (didDrag) {
          event.preventDefault();
          event.stopPropagation();
          didDrag = false;
          return;
        }

        const originalIndex = Number.parseInt(item.dataset.originalIndex || '0', 10);
        this.#openLightbox(originalIndex);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const originalIndex = Number.parseInt(item.dataset.originalIndex || '0', 10);
          this.#openLightbox(originalIndex);
        }
      });
    });
  }

  #normalizeLoopPosition() {
    if (this.originalCount <= 6 || this._jumping) {
      return;
    }

    const selectedIndex = this.carousel.selectedIndex ?? this.activeIndex;

    if (selectedIndex >= this.originalCount) {
      this._jumping = true;
      const normalized = selectedIndex % this.originalCount;
      this.activeIndex = normalized;
      this.carousel.select(normalized, { instant: true });
      requestAnimationFrame(() => {
        this._jumping = false;
      });
    }
  }

  #canAutoScroll() {
    return (
      this.originalCount > 2 &&
      !this.lightbox?.open &&
      document.visibilityState === 'visible'
    );
  }

  #syncAutoScroll() {
    if (this.#canAutoScroll()) {
      this.#scheduleAutoScroll();
    } else {
      this.#stopAutoScroll();
    }
  }

  #scheduleAutoScroll() {
    this.#stopAutoScroll();

    if (!this.#canAutoScroll()) {
      return;
    }

    this._timer = window.setTimeout(() => {
      this.#advance();
      this.#scheduleAutoScroll();
    }, ProductMetafieldGallerySequence.AUTO_SCROLL_MS);
  }

  #stopAutoScroll() {
    if (this._timer) {
      window.clearTimeout(this._timer);
      this._timer = null;
    }
  }

  #advance() {
    if (!this.originalCount) {
      return;
    }

    const current = this.carousel.selectedIndex ?? this.activeIndex;
    const totalCells = this.carousel.cells?.length || this.originalCount;
    const nextIndex = this.originalCount > 6
      ? current + 1
      : (current + 1) % this.originalCount;

    if (nextIndex >= totalCells && this.originalCount > 6) {
      this.activeIndex = 0;
      this.carousel?.select(0, { instant: true });
      return;
    }

    this.activeIndex = nextIndex;
    this.carousel?.select(nextIndex, { instant: false });
  }

  #openLightbox(index) {
    if (!this.originalCount) {
      return;
    }

    this.#stopAutoScroll();
    this.#ensureLightbox();
    this.#showSlide(index);
    this.lightbox.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  #closeLightbox() {
    if (!this.lightbox?.open) {
      return;
    }

    const stageVideo = this.lightbox.querySelector('video');
    stageVideo?.pause();
    this.lightbox.close();
    document.documentElement.style.overflow = '';
    this.#syncAutoScroll();
  }

  #ensureLightbox() {
    if (this.lightbox) {
      return;
    }

    const dialog = document.createElement('dialog');
    dialog.className = 'product-videos-lightbox';
    dialog.innerHTML = `
      <div class="product-videos-lightbox__inner">
        <button type="button" class="product-videos-lightbox__close" aria-label="${this.#label('close')}">
          <svg aria-hidden="true" focusable="false" fill="none" width="14" viewBox="0 0 16 16">
            <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </button>
        <button type="button" class="product-videos-lightbox__nav" data-dir="prev" aria-label="${this.#label('previous')}">
          <svg aria-hidden="true" focusable="false" width="12" viewBox="0 0 36 36">
            <path fill="none" stroke="currentColor" stroke-width="2" d="M35 18H3M19.5 34.5 3 18 19.5 1.5"/>
          </svg>
        </button>
        <div class="product-videos-lightbox__stage"></div>
        <button type="button" class="product-videos-lightbox__nav" data-dir="next" aria-label="${this.#label('next')}">
          <svg aria-hidden="true" focusable="false" width="12" viewBox="0 0 36 36">
            <path fill="none" stroke="currentColor" stroke-width="2" d="M1 18h32M16.5 1.5 33 18 16.5 34.5"/>
          </svg>
        </button>
        <div class="product-videos-lightbox__counter" aria-live="polite"></div>
      </div>
    `;

    dialog.querySelector('.product-videos-lightbox__close').addEventListener('click', () => this.#closeLightbox());
    dialog.querySelector('[data-dir="prev"]').addEventListener('click', () => this.#stepLightbox(-1));
    dialog.querySelector('[data-dir="next"]').addEventListener('click', () => this.#stepLightbox(1));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        this.#closeLightbox();
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeLightbox();
    });

    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      const stageVideo = dialog.querySelector('video');
      stageVideo?.pause();
      this.#syncAutoScroll();
    });

    this._onKeydown = (event) => {
      if (!dialog.open) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#stepLightbox(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#stepLightbox(1);
      }
    };

    document.addEventListener('keydown', this._onKeydown);
    document.body.appendChild(dialog);
    this.lightbox = dialog;

    if (this.originalCount <= 1) {
      dialog.querySelectorAll('.product-videos-lightbox__nav').forEach((button) => {
        button.hidden = true;
      });
    }
  }

  #destroyLightbox() {
    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown);
    }

    this.lightbox?.remove();
    this.lightbox = null;
    document.documentElement.style.overflow = '';
  }

  #stepLightbox(delta) {
    if (!this.originalCount) {
      return;
    }

    const nextIndex = (this.lightboxIndex + delta + this.originalCount) % this.originalCount;
    this.#showSlide(nextIndex);
  }

  #showSlide(index) {
    const item = this.originalItems[index];
    const stage = this.lightbox?.querySelector('.product-videos-lightbox__stage');
    const counter = this.lightbox?.querySelector('.product-videos-lightbox__counter');

    if (!item || !stage) {
      return;
    }

    this.lightboxIndex = index;

    const type = item.dataset.lightboxType || item.dataset.mediaType || 'image';
    const src = item.dataset.lightboxSrc || '';
    const width = item.dataset.lightboxWidth || '';
    const height = item.dataset.lightboxHeight || '';
    const poster = item.dataset.lightboxPoster || '';
    const mime = item.dataset.lightboxMime || 'video/mp4';

    stage.replaceChildren();

    if (!src) {
      return;
    }

    if (type === 'video') {
      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.autoplay = true;
      video.setAttribute('playsinline', '');

      if (poster) {
        video.poster = poster;
      }

      if (width) {
        video.width = Number.parseInt(width, 10);
      }

      if (height) {
        video.height = Number.parseInt(height, 10);
      }

      const source = document.createElement('source');
      source.src = src;
      source.type = mime;
      video.appendChild(source);
      stage.appendChild(video);
      video.play?.().catch(() => {});
    } else {
      const image = document.createElement('img');
      image.src = src;
      image.alt = '';
      image.loading = 'eager';

      if (width) {
        image.width = Number.parseInt(width, 10);
      }

      if (height) {
        image.height = Number.parseInt(height, 10);
      }

      stage.appendChild(image);
    }

    if (counter) {
      counter.textContent = `${index + 1} / ${this.originalCount}`;
    }
  }

  #label(key) {
    const strings = window.themeVariables?.strings || {};

    if (key === 'close') {
      return strings.closeGallery || strings.close || 'Close';
    }

    if (key === 'previous') {
      return strings.previous || 'Previous';
    }

    if (key === 'next') {
      return strings.next || 'Next';
    }

    return key;
  }
}

if (!window.customElements.get('product-metafield-gallery-sequence')) {
  window.customElements.define('product-metafield-gallery-sequence', ProductMetafieldGallerySequence);
}
